import {
  CalendarDays,
  CheckCircle2,
  Keyboard,
  LockKeyhole,
  MapPin,
  ShieldAlert,
  Ticket as TicketIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { adminApi } from "../../../api/client";
import TicketScanner from "../../../components/admin/TicketScanner";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import type { EventItem } from "../../../types/api";

const RECENT_SCAN_DEDUP_MS = 3000;

function parseTicketCode(raw: string) {
  try {
    const parsed = JSON.parse(raw) as { ticketCode?: string };
    return parsed.ticketCode?.trim().toUpperCase() ?? raw.trim();
  } catch {
    return raw.trim().toUpperCase();
  }
}

function formatEventDate(value: string) {
  if (!value) return "Date TBA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type ScanResult = {
  type: "success" | "error";
  message: string;
  orderReference?: string;
  eventTitle?: string;
  attendeeName?: string;
  status?: string;
  checkedInAt?: string;
};

export default function AdminScannerPage() {
  const { admin, token } = useAdminAuth();
  const [manualCode, setManualCode] = useState("");
  const [history, setHistory] = useState<ScanResult[]>([]);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [eventQuery, setEventQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const inFlightRef = useRef(false);
  const lastScanRef = useRef<{ code: string; eventId: string; at: number } | null>(null);
  const canScan = Boolean(admin?.effectivePermissions?.includes("tickets:scan"));

  useEffect(() => {
    if (!token || !canScan) {
      setEventsLoading(false);
      return;
    }

    let active = true;
    setEventsLoading(true);
    setEventsError(null);

    adminApi
      .listEvents(token)
      .then((response) => {
        if (active) setEvents(response.events);
      })
      .catch((requestError) => {
        if (!active) return;
        setEventsError(requestError instanceof Error ? requestError.message : "Could not load screenings");
      })
      .finally(() => {
        if (active) setEventsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canScan, token]);

  const filteredEvents = useMemo(() => {
    const term = eventQuery.trim().toLowerCase();
    if (!term) return events;
    return events.filter((event) => {
      return (
        event.title.toLowerCase().includes(term) ||
        event.city.toLowerCase().includes(term) ||
        event.venue.toLowerCase().includes(term)
      );
    });
  }, [eventQuery, events]);

  const openEventScanner = (event: EventItem) => {
    setSelectedEvent(event);
    setManualCode("");
    setResult(null);
    setHistory([]);
    lastScanRef.current = null;
  };

  const closeEventScanner = () => {
    setSelectedEvent(null);
    setManualCode("");
    setResult(null);
    setHistory([]);
    lastScanRef.current = null;
  };

  const handleScan = useCallback(
    async (payload: string) => {
      if (!token || !selectedEvent?.id) return;

      const code = parseTicketCode(payload);
      if (!code) return;

      const eventId = selectedEvent.id;

      // Drop concurrent calls while the previous request is still open.
      if (inFlightRef.current) return;

      // Drop the same code for the same screening if it was just scanned.
      const now = Date.now();
      const last = lastScanRef.current;
      if (
        last &&
        last.code === code &&
        last.eventId === eventId &&
        now - last.at < RECENT_SCAN_DEDUP_MS
      ) {
        return;
      }

      inFlightRef.current = true;
      setIsScanning(true);
      let next: ScanResult;

      try {
        const response = await adminApi.scanTicket(token, code, eventId);
        next = {
          type: "success",
          message: response.message,
          orderReference: response.orderReference,
          eventTitle: response.event.title,
          attendeeName: response.ticket.attendeeName,
          status: response.ticket.status,
          checkedInAt: response.ticket.checkedInAt,
        };
      } catch (requestError) {
        next = {
          type: "error",
          message: requestError instanceof Error ? requestError.message : "Ticket scan failed",
        };
      } finally {
        inFlightRef.current = false;
        setIsScanning(false);
        lastScanRef.current = { code, eventId, at: Date.now() };
      }

      setResult(next);
      setHistory((current) => [next, ...current].slice(0, 6));
    },
    [selectedEvent?.id, token]
  );

  if (!canScan) {
    return (
      <div className="ego-surface px-6 py-12 text-center">
        <ShieldAlert size={34} className="mx-auto text-[#b91c1c]" />
        <h2 className="mt-3 text-lg font-bold text-foreground">Scanner access unavailable</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          Ticket check-in is only available to invited scanner accounts.
        </p>
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div className="space-y-6">
        <div className="ego-surface p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]">
              <LockKeyhole size={18} />
            </div>
            <div>
              <div className="ego-eyebrow">Scanner screening</div>
              <h2 className="mt-1 text-xl font-bold text-foreground">Select the screening to scan</h2>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                The camera and manual lookup stay locked to the selected screening until you change it.
              </p>
            </div>
          </div>
        </div>

        <div className="ego-surface overflow-hidden">
          <div className="border-b border-line px-5 py-4">
            <input
              value={eventQuery}
              onChange={(event) => setEventQuery(event.target.value)}
              placeholder="Search screening by title, venue or city"
              className="ego-input"
            />
          </div>

          {eventsLoading ? (
            <div className="px-5 py-10 text-center text-sm font-semibold text-muted-foreground">Loading screenings...</div>
          ) : eventsError ? (
            <div className="px-5 py-10 text-center text-sm font-semibold text-[#b91c1c]">{eventsError}</div>
          ) : filteredEvents.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm font-semibold text-muted-foreground">No screenings found.</div>
          ) : (
            <div className="ego-divide">
              {filteredEvents.map((event) => (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => openEventScanner(event)}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left transition hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pp-gold)]"
                >
                  <img
                    src={event.image}
                    alt={event.title}
                    className="h-16 w-20 rounded-lg object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-foreground">{event.title}</div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays size={12} /> {formatEventDate(event.date)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} /> {event.venue}, {event.city}
                      </span>
                    </div>
                  </div>
                  <span className="ego-badge ego-badge-neutral capitalize">{event.status.replace("-", " ")}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="ego-surface flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#ecfdf5] dark:bg-[rgba(16,185,129,0.14)] text-[#047857]">
            <LockKeyhole size={18} />
          </div>
          <div>
            <div className="ego-eyebrow">Scanner locked to</div>
            <h2 className="mt-1 text-xl font-bold text-foreground">{selectedEvent.title}</h2>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <CalendarDays size={13} /> {formatEventDate(selectedEvent.date)}
              </span>
              <span className="inline-flex items-center gap-1">
                <MapPin size={13} /> {selectedEvent.venue}, {selectedEvent.city}
              </span>
            </div>
          </div>
        </div>
        <button type="button" onClick={closeEventScanner} className="ego-btn-secondary">
          Change screening
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <TicketScanner onDecode={handleScan} />

        <div className="space-y-6">
          <div className="ego-surface overflow-hidden">
            <div className="border-b border-line px-5 py-4">
              <div className="flex items-center gap-2">
                <Keyboard size={15} className="text-muted-foreground" />
                <div className="ego-eyebrow">Manual lookup</div>
              </div>
              <h3 className="mt-1 text-lg font-bold text-foreground">Enter a ticket code</h3>
            </div>
            <div className="space-y-3 p-5">
              <input
                value={manualCode}
                onChange={(event) => setManualCode(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void handleScan(manualCode);
                }}
                placeholder="e.g. PP-A1B2C3D4"
                className="ego-input font-mono uppercase tracking-[0.18em]"
              />
              <button
                type="button"
                onClick={() => void handleScan(manualCode)}
                disabled={isScanning || !manualCode.trim()}
                className="ego-btn-primary w-full"
              >
                {isScanning ? "Validating..." : "Validate ticket"}
              </button>
            </div>
          </div>

          <div className="ego-surface overflow-hidden">
            <div className="border-b border-line px-5 py-4">
              <div className="ego-eyebrow">Last result</div>
            </div>
            {result ? (
              <div className="p-5">
                <div
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 ${
                    result.type === "success"
                      ? "bg-[#ecfdf5] dark:bg-[rgba(16,185,129,0.14)] text-[#047857]"
                      : "bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] text-[#b91c1c]"
                  }`}
                >
                  {result.type === "success" ? (
                    <CheckCircle2 size={22} strokeWidth={2.4} />
                  ) : (
                    <ShieldAlert size={22} strokeWidth={2.4} />
                  )}
                  <div className="text-sm font-bold">{result.message}</div>
                </div>

                {result.type === "success" && (
                  <dl className="mt-4 grid gap-2 text-sm">
                    {result.eventTitle && (
                      <div className="flex justify-between gap-3 border-b border-line pb-2">
                        <dt className="text-muted-foreground">Screening</dt>
                        <dd className="text-right font-semibold text-foreground">{result.eventTitle}</dd>
                      </div>
                    )}
                    {result.attendeeName && (
                      <div className="flex justify-between gap-3 border-b border-line pb-2">
                        <dt className="text-muted-foreground">Attendee</dt>
                        <dd className="text-right font-semibold text-foreground">{result.attendeeName}</dd>
                      </div>
                    )}
                    {result.orderReference && (
                      <div className="flex justify-between gap-3 border-b border-line pb-2">
                        <dt className="text-muted-foreground">Order</dt>
                        <dd className="text-right font-mono text-xs font-semibold text-foreground">
                          {result.orderReference}
                        </dd>
                      </div>
                    )}
                    {result.status && (
                      <div className="flex justify-between gap-3 border-b border-line pb-2">
                        <dt className="text-muted-foreground">Status</dt>
                        <dd className="text-right">
                          <span className="ego-badge ego-badge-success uppercase">{result.status}</span>
                        </dd>
                      </div>
                    )}
                    {result.checkedInAt && (
                      <div className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">Verified</dt>
                        <dd className="text-right text-foreground">
                          {new Date(result.checkedInAt).toLocaleString()}
                        </dd>
                      </div>
                    )}
                  </dl>
                )}
              </div>
            ) : (
              <div className="px-5 py-8 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-surface-muted text-muted-foreground">
                  <TicketIcon size={18} />
                </div>
                <p className="text-sm text-muted-foreground">Scan a ticket to see validation feedback here.</p>
              </div>
            )}
          </div>

          {history.length > 1 && (
            <div className="ego-surface overflow-hidden">
              <div className="border-b border-line px-5 py-4">
                <div className="ego-eyebrow">Recent scans</div>
              </div>
              <div className="ego-divide">
                {history.slice(1).map((item, index) => (
                  <div key={index} className="flex items-center gap-3 px-5 py-3 text-sm">
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-full ${
                        item.type === "success" ? "bg-[#ecfdf5] dark:bg-[rgba(16,185,129,0.14)] text-[#047857]" : "bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] text-[#b91c1c]"
                      }`}
                    >
                      {item.type === "success" ? <CheckCircle2 size={14} /> : <ShieldAlert size={14} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold text-foreground">{item.attendeeName ?? item.message}</div>
                      {item.eventTitle && (
                        <div className="truncate text-xs text-muted-foreground">{item.eventTitle}</div>
                      )}
                    </div>
                    {item.orderReference && (
                      <span className="ego-badge ego-badge-neutral font-mono">{item.orderReference}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
